// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type ProductPoolId,
  type CompanyMember,
  type Entitlement,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePaginatedGetAll,
  ignoreCanceledApiRequest,
  type GetPaginatedAllOpts,
} from '@evolvemep/api-ts';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { WrappedMultiSelect } from 'components/Mantine/TypeSafeSelect/WrappedMultiSelect';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { PRODUCT_IDS } from 'constants/globalConstants';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { getMultiRowSelection } from 'helpers/ag-grid/baseColumnDef';
import { detailTable } from 'helpers/ag-grid/DetailTables/detailTable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongValues } from 'helpers/strongEntries';
import { useProductPoolLicenseCheck } from 'hooks/useProductPoolLicenseCheck';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { useUser } from 'hooks/user/useUser';
import { AddLicensesModal } from 'modules/Admin/CompanyMembers/AddLicensesModal';
import {
  DeleteMembersModal,
  AddAdminModal,
  RemoveAdminModal,
  RemoveAllRolesFromUserModal,
  RemoveRoleListFromUserModal,
  RemoveLicenseModal,
  PasswordResetModal,
} from 'modules/Admin/CompanyMembers/AdminActionModals';
import { AssignRolesModal } from 'modules/Admin/CompanyMembers/AssignRolesModal';
import { getCompanyMembersColDef, getEntitlementsColDef } from 'modules/Admin/CompanyMembers/columnDefs';
import { type CompanyMembersBulkAction, getBulkActions } from 'modules/Admin/CompanyMembers/CompanyMemberMenu';
import { ManageRevitRolesModal } from 'modules/Admin/CompanyMembers/ManageRevitRolesModal';
import { PermissionGuideModal } from 'modules/Admin/CompanyMembers/PermissionGuideModal';
import type { RoleToRemove, HasProductsAvailable } from 'modules/Admin/CompanyMembers/types';
import { InviteUsersModal } from 'modules/Shop/ShopMembers/InviteUsersModal';

const { MECHANICAL_LICENSE, ELECTRICAL_LICENSE, SHOP_LICENSE, REVIT_ROLES } = PRODUCT_IDS;

export const CompanyMembersPage = () => {
  const { user, licenses } = useUser();
  const hasModifyCompanyMembersAccess = useHasAccess('companyMembers', 'write');

  const [showNoLicense, setShowNoLicense] = useState(false);
  const [selectedProductPools, setSelectedProductPools] = useState<ProductPoolId[]>([]);

  const productPoolOpts = useMemo<GetPaginatedAllOpts<'admin/productPool'>>(
    () => ({
      lazy: false,
      defaultConfig: {
        params: {
          companyId: user.companyId,
          excludeExpired: true,
        },
      },
    }),
    [user.companyId],
  );

  const {
    apiCall: refreshProductPool,
    data: productPools,
    loading: loadingProductPools,
    setDefaultOpts: setDefaultProductPoolOpts,
  } = useEvolvePaginatedGetAll('admin/productPool', productPoolOpts);

  useEffect(() => {
    setDefaultProductPoolOpts(productPoolOpts);
  }, [setDefaultProductPoolOpts, productPoolOpts]);

  const { productPoolsActive } = useProductPoolLicenseCheck();

  const hasProducts = useMemo<HasProductsAvailable>(
    () => ({
      revitRoles: licenses.some((productId) => productId === REVIT_ROLES),
      shop: licenses.some((productId) => productId === SHOP_LICENSE),
      // These licenses aren't company-wide, but we want to show columns based on whether they're available to the company.
      // So we filter by product pool to determine availability.
      electrical: productPoolsActive.has(ELECTRICAL_LICENSE),
      mechanical: productPoolsActive.has(MECHANICAL_LICENSE),
    }),
    [licenses, productPoolsActive],
  );

  const { fetchPage, abortAll, setDefaultOpts } = useEvolvePaginatedGet('admin/company/members');
  useEffect(() => {
    setDefaultOpts({
      defaultConfig: {
        params: {
          orderBy: 'email:asc',
          companyId: user.companyId,
          productPoolId: selectedProductPools,
          hasLicense: showNoLicense ? false : undefined,
        },
      },
    });
  }, [setDefaultOpts, selectedProductPools, showNoLicense, user.companyId]);

  const [singleUserAction, setSingleUserAction] = useState<CompanyMember | null>(null);
  const [removeEntitlement, setRemoveEntitlement] = useState<Entitlement | null>(null);
  const [removeRole, setRemoveRole] = useState<RoleToRemove | null>(null);
  const [selectedRows, setSelectedRows] = useState<CompanyMember[]>([]);
  const [selectedAction, setSelectedAction] = useState<CompanyMembersBulkAction | null>(null);

  const { apiCall: getCompanyApiCall, data: company } = useEvolveGet(`admin/company/${user.companyId}`, {
    lazy: false,
  });

  const { fetchPage: fetchEntitlementPage } = useEvolvePaginatedGet('admin/entitlement/multisearch');

  const { data: assignableRoles, apiCall: fetchAssignableRoles } = useEvolveGet('admin/role/assignable', {
    lazy: false,
  });

  const actionSingleUser = useCallback((selectedUser: CompanyMember, action: CompanyMembersBulkAction) => {
    setSelectedAction(action);
    setSingleUserAction(selectedUser);
  }, []);
  const usersToAction = useMemo(
    () => (singleUserAction ? [singleUserAction] : selectedRows),
    [selectedRows, singleUserAction],
  );
  useEffect(() => {
    if (isNil(selectedAction)) {
      setSingleUserAction(null);
    }
  }, [selectedAction]);

  const colDef = useMemo(
    () =>
      getCompanyMembersColDef({
        hasModifyCompanyMembersAccess,
        actionSingleUser,
        roles: assignableRoles ?? [],
        setRemoveRole,
        hasProducts,
      }),
    [hasModifyCompanyMembersAccess, actionSingleUser, assignableRoles, hasProducts],
  );

  const entitlementsColDef = useMemo(
    () => getEntitlementsColDef(hasModifyCompanyMembersAccess, setRemoveEntitlement),
    [hasModifyCompanyMembersAccess],
  );

  const { agGridProps, expandDetailGrid, refreshDetailGrid, gridApi } = useServerSideGrid({
    tableName: 'company-members',
    tableViewType: TABLE_VIEW_TYPE.MEMBERS,
    exportFileName: 'Evolve Members',
    fetchPage,
    abortAll,
    colDef,
    rowId: 'userId',
    detailTables: [
      detailTable({
        title: 'Licenses',
        hideHeader: true,
        colDef: entitlementsColDef,
        fetchPage: fetchEntitlementPage,
        icon: 'Licenses',
        autoExpand: true,
        configMapper: (d: CompanyMember) => ({ params: { userId: d.userId } }),
      }),
    ],
  });

  const clearSelection = useCallback(() => {
    agGridProps.ref.current?.api.deselectAll();
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [agGridProps.ref]);

  const defaultColDef = useMemo<(typeof agGridProps)['defaultColDef']>(
    () => ({
      ...agGridProps.defaultColDef,
      cellClassRules: {
        'disabled-row': (r) => r.data?.status === 'Deleted',
      },
    }),
    [agGridProps.defaultColDef],
  );

  const rowSelection = useMemo(
    () =>
      getMultiRowSelection<CompanyMember>({
        checkboxes: (d) => d.data?.status !== 'Deleted',
      }),
    [],
  );

  const dropdownData: { value: CompanyMembersBulkAction; label: string; disabled?: boolean }[] = useMemo(
    () =>
      getBulkActions({
        userIsMyUser: hasLengthExactly(selectedRows, 1) && selectedRows[0].userId === user.userId,
        userIsAdmin: !selectedRows.some((r) => !r.isAdmin),
        companyHasRoles: strongValues(hasProducts).some((isEnabled) => isEnabled),
        userIsActive: selectedRows.some((r) => r.status === 'Active'),
      })
        .filter(({ value, hidden }) => value !== 'Remove roles' && value !== 'Resend invite' && !hidden)
        .map((o) => ({
          ...o,
          label: o.value,
          color: o.value === 'Delete' ? 'red' : undefined,
        })),
    [hasProducts, selectedRows, user.userId],
  );

  const addOrRemoveAdminCompleted = () => {
    refreshGrid(gridApi);
    clearSelection();
    setSelectedAction(null);
  };

  const handleCloseActionModal = useCallback(() => {
    setSelectedAction(null);
  }, []);

  return (
    <>
      <BasePageHeader
        title="Members"
        noHistory
        topRightComponent={
          <>
            {isNotNil(assignableRoles) && assignableRoles.length > 0 && hasModifyCompanyMembersAccess && (
              <ManageRevitRolesModal
                roles={assignableRoles}
                hasRevitRoles={hasProducts.revitRoles}
                hasElectrical={hasProducts.electrical}
                hasMechanical={hasProducts.mechanical}
                company={company}
                refetch={() => {
                  refreshGrid(gridApi);
                  void fetchAssignableRoles();
                  void getCompanyApiCall();
                }}
              />
            )}
            <PermissionGuideModal />
            {hasModifyCompanyMembersAccess && <InviteUsersModal refresh={() => refreshGrid(gridApi)} />}
          </>
        }
        bottomLeftComponent={
          hasModifyCompanyMembersAccess ? (
            <WrappedSelect
              placeholder={selectedRows.length > 0 ? `Action (${selectedRows.length})` : 'Action'}
              disabled={selectedRows.length === 0}
              value={selectedAction}
              data={dropdownData}
              onChange={setSelectedAction}
            />
          ) : undefined
        }
        bottomRightComponent={
          <WrappedMultiSelect<'No Licenses' | ProductPoolId>
            value={showNoLicense ? ['No Licenses'] : selectedProductPools}
            placeholder="Filter by license..."
            data={[
              {
                group: '',
                items: [
                  {
                    label: 'No User Licenses',
                    value: 'No Licenses' as const,
                  },
                ],
              },
              {
                group: 'Licenses',
                items:
                  productPools?.map((p) => ({
                    label: p.product.productNameFormatted,
                    value: p.productPoolId,
                  })) ?? [],
              },
            ]}
            onChange={(selected) => {
              if (showNoLicense || !selected.includes('No Licenses')) {
                setShowNoLicense(false);
                setSelectedProductPools(selected.filter((s): s is ProductPoolId => s !== 'No Licenses'));
              } else if (selected.includes('No Licenses')) {
                setShowNoLicense(true);
                setSelectedProductPools([]);
              }
            }}
            comboboxProps={{
              styles: {
                option: { marginTop: 1, marginBottom: 1 },
              },
            }}
            clearable
            style={{ width: 250 }}
          />
        }
      />

      <AgGridContainer>
        {(applyWrap) => (
          <AgGridReact<CompanyMember>
            {...agGridProps}
            onFirstDataRendered={applyWrap}
            defaultColDef={defaultColDef}
            rowSelection={hasModifyCompanyMembersAccess ? rowSelection : undefined}
            onSelectionChanged={({ api }) => {
              setSelectedRows(api.getSelectedRows());
            }}
            noRowsOverlayComponent={() => <NoRowsOverlay label="No members found." gridApi={gridApi} />}
          />
        )}
      </AgGridContainer>
      {hasModifyCompanyMembersAccess && (
        <>
          <AddLicensesModal
            productPools={productPools ?? []}
            loadingProductPools={loadingProductPools}
            userIds={usersToAction.map((r) => r.userId)}
            opened={selectedAction === 'Assign licenses'}
            onClose={() => setSelectedAction(null)}
            refresh={() => {
              usersToAction.forEach((r) => {
                refreshDetailGrid(r.userId, 'Licenses');
                expandDetailGrid(r.userId, 'Licenses');
              });
              refreshGrid(gridApi);
              clearSelection();
              void refreshProductPool().catch(ignoreCanceledApiRequest);
            }}
          />

          <AssignRolesModal
            userIds={
              isNotNil(singleUserAction)
                ? [singleUserAction.userId]
                : usersToAction.filter((u) => !u.isAdmin).map((r) => r.userId)
            }
            opened={selectedAction === 'Assign roles'}
            assignableRoles={assignableRoles ?? []}
            onClose={() => setSelectedAction(null)}
            hasProducts={hasProducts}
            refresh={() => {
              refreshGrid(gridApi);
              clearSelection();
            }}
          />

          <RemoveRoleListFromUserModal
            removeRole={removeRole}
            endRemoveRole={() => {
              setRemoveRole(null);
            }}
            onRoleRemoved={() => refreshGrid(gridApi)}
            assignableRoles={assignableRoles}
          />

          <RemoveAllRolesFromUserModal
            opened={selectedAction === 'Remove roles'}
            onClose={handleCloseActionModal}
            singleUserAction={singleUserAction}
            onSuccess={() => {
              if (isNotNil(singleUserAction)) {
                refreshGrid(gridApi);
                setSingleUserAction(null);
              }
            }}
          />

          <DeleteMembersModal
            opened={selectedAction === 'Delete'}
            onClose={handleCloseActionModal}
            usersToAction={usersToAction}
            user={user}
            onSuccess={() => {
              clearSelection();
              refreshGrid(gridApi);
            }}
          />

          <AddAdminModal
            opened={selectedAction === 'Assign admin'}
            onClose={handleCloseActionModal}
            onSuccess={addOrRemoveAdminCompleted}
            usersToAction={usersToAction}
          />

          <RemoveAdminModal
            opened={selectedAction === 'Remove admin'}
            onClose={handleCloseActionModal}
            onSuccess={addOrRemoveAdminCompleted}
            usersToAction={usersToAction}
          />

          <PasswordResetModal
            opened={selectedAction === 'Reset password'}
            onClose={handleCloseActionModal}
            usersToAction={usersToAction}
          />

          <RemoveLicenseModal
            entitlement={removeEntitlement}
            onClose={() => setRemoveEntitlement(null)}
            onSuccess={() => {
              if (removeEntitlement) {
                refreshDetailGrid(removeEntitlement.userId);
              }
              void refreshProductPool().catch(ignoreCanceledApiRequest);
            }}
          />
        </>
      )}
    </>
  );
};
